import React, { useState, useRef, useEffect } from 'react';
import {
  Combobox,
  Portal,
  useCombobox,
  useListCollection,
  Spinner,
  For,
  HStack,
  Span,
} from '@chakra-ui/react';

// SearchableSelect styles using design tokens (matching Select component)
const searchableSelectControlStyles = {
  bg: "white",
  _dark: { bg: "gray.700" },
  borderColor: "gray.300",
  _hover: { borderColor: "gray.400" },
  _focus: { 
    borderColor: "brand.600", 
    boxShadow: "0 0 0 1px var(--chakra-colors-brand-600)" 
  },
};

const searchableSelectContentStyles = {
  bg: "white",
  borderColor: "gray.200",
  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  zIndex: 9999,
  _dark: { 
    bg: "gray.700", 
    borderColor: "gray.600" 
  },
};

const searchableSelectItemStyles = {
  _hover: { 
    bg: "brand.50", 
    _dark: { bg: "brand.900" } 
  },
  _selected: { 
    bg: "brand.100",
    color: "brand.700",
    _dark: { bg: "brand.800", color: "brand.200" } 
  },
};

const searchableSelectLabelStyles = {
  fontSize: "sm",
  fontWeight: "medium",
  color: "gray.700",
  _dark: { color: "gray.300" },
  mb: 2,
};

const searchableSelectInputStyles = {
  outline: "none",
  _focus: {
    outline: "none",
    borderColor: "brand.600",
    boxShadow: "0 0 0 1px var(--chakra-colors-brand-600)"
  },
};

// Simple useAsync implementation with debouncing matching the official example pattern
const useAsync = (asyncFunction: () => Promise<any>, deps: any[], debounceMs = 300) => {
  const [state, setState] = useState<{ loading: boolean; error: Error | null; value: any }>({ 
    loading: false, 
    error: null, 
    value: null 
  });
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setState({ loading: true, error: null, value: null });
      
      asyncFunction()
        .then(value => setState({ loading: false, error: null, value }))
        .catch(error => setState({ loading: false, error: error as Error, value: null }));
    }, debounceMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, deps);

  return state;
};

export interface SearchableSelectOption {
  value: string;
  label: string;
}

export interface SearchableSelectProps {
  value?: string;
  onChange?: (value: string) => void;
  onOptionSelect?: (option: SearchableSelectOption) => void;
  onSearch: (query: string) => Promise<SearchableSelectOption[]>;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  initialOptions?: SearchableSelectOption[];
  selectedOption?: SearchableSelectOption | null;
  minSearchLength?: number;
  debounceMs?: number;
  loadingText?: string;
  noResultsText?: string;
  size?: "xs" | "sm" | "md" | "lg";
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  onOptionSelect,
  onSearch,
  placeholder = "Search and select...",
  label,
  disabled = false,
  initialOptions = [],
  selectedOption = null,
  minSearchLength = 1,
  debounceMs = 300,
  loadingText = "Searching...",
  noResultsText = "No results found",
  size = "sm",
}) => {
  const [inputValue, setInputValue] = useState("");

  // Create collection for Combobox options
  const { collection, set } = useListCollection<SearchableSelectOption>({
    initialItems: [],
    itemToString: (item) => item.label,
    itemToValue: (item) => item.value,
  });

  // Setup combobox with the collection
  const combobox = useCombobox({
    collection,
    defaultValue: selectedOption ? [selectedOption.value] : [],
    placeholder,
    inputValue,
    onInputValueChange: (details) => setInputValue(details.inputValue),
    onValueChange: (details) => {
      const selectedValue = details.value[0] || "";
      onChange?.(selectedValue);
      
      if (selectedValue) {
        // Find the selected option and call onOptionSelect
        const selectedOpt = collection.items.find(opt => opt.value === selectedValue);
        if (selectedOpt) {
          onOptionSelect?.(selectedOpt);
        }
      }
    },
  });

  // Search state using useAsync (exact pattern from official docs)
  const state = useAsync(async () => {
    if (inputValue.length < minSearchLength && inputValue.length > 0) {
      return [];
    }
    
    const results = await onSearch(inputValue);
    
    // If selectedOption is not in the list, add it
    let finalResults = results;
    if (selectedOption && !results.find(item => item.value === selectedOption.value)) {
      finalResults = [selectedOption, ...results];
    }
    
    set(finalResults);
    return finalResults;
  }, [inputValue, onSearch, minSearchLength, set], debounceMs);

  // Initialize with selectedOption if provided (like the official example)
  useEffect(() => {
    
    if (selectedOption && collection.items.length === 0) {
      set([selectedOption]);
    }
    
  }, [selectedOption, set, collection.items.length]);

  // Rehydrate the value (exact pattern from official docs)
  const hydrated = useRef(false);
  if (combobox.value.length && collection.size && !hydrated.current) {
    combobox.syncSelectedItems();
    hydrated.current = true;
  }

  return (
    <Combobox.RootProvider value={combobox} size={size}>
      {label && <Combobox.Label {...searchableSelectLabelStyles}>{label}</Combobox.Label>}

      <Combobox.Control {...searchableSelectControlStyles}>
        <Combobox.Input 
          placeholder={placeholder} 
          disabled={disabled}
          {...searchableSelectInputStyles}
        />
        <Combobox.IndicatorGroup>
          <Combobox.ClearTrigger />
          <Combobox.Trigger />
        </Combobox.IndicatorGroup>
      </Combobox.Control>

      <Portal>
        <Combobox.Positioner zIndex={9999}>
          <Combobox.Content {...searchableSelectContentStyles}>
            {state.loading ? (
              <HStack p="2">
                <Spinner size="xs" />
                <Span>{loadingText}</Span>
              </HStack>
            ) : state.error ? (
              <Span p="2" color="fg.error">
                {state.error.message}
              </Span>
            ) : (
              <For
                each={collection.items}
                fallback={<Combobox.Empty>{noResultsText}</Combobox.Empty>}
              >
                {(item) => (
                  <Combobox.Item key={item.value} item={item} {...searchableSelectItemStyles}>
                    {item.label}
                    <Combobox.ItemIndicator />
                  </Combobox.Item>
                )}
              </For>
            )}
          </Combobox.Content>
        </Combobox.Positioner>
      </Portal>
    </Combobox.RootProvider>
  );
};

export default SearchableSelect;